import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dumbbell, Brain, Clock, Trash2,
  Pencil, X, Check, ListPlus
} from "lucide-react";
import { toast } from "sonner";

export const PHYSICAL_CATEGORIES = [
  { value: "cardio", label: "Cardio" },
  { value: "strength", label: "Strength" },
  { value: "flexibility", label: "Flexibility" },
  { value: "balance", label: "Balance" },
];

export const COGNITIVE_CATEGORIES = [
  { value: "focus", label: "Focus" },
  { value: "memory", label: "Memory" },
  { value: "creativity", label: "Creativity" },
  { value: "mindfulness", label: "Mindfulness" },
];

export const INTENSITIES = [
  { value: "low", label: "Low", color: "text-emerald-500 bg-emerald-50" },
  { value: "medium", label: "Medium", color: "text-amber-500 bg-amber-50" },
  { value: "high", label: "High", color: "text-red-500 bg-red-50" },
];

export const ExerciseForm = ({ exercise, onSave, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: exercise?.name || "",
    exercise_type: exercise?.exercise_type || "physical",
    category: exercise?.category || "cardio",
    duration_minutes: exercise?.duration_minutes || 15,
    intensity: exercise?.intensity || "medium",
    description: exercise?.description || "",
    instructions: exercise?.instructions || "",
  });
  const [saving, setSaving] = useState(false);

  const categories = form.exercise_type === "physical" ? PHYSICAL_CATEGORIES : COGNITIVE_CATEGORIES;

  useEffect(() => {
    if (form.exercise_type === "physical" && !PHYSICAL_CATEGORIES.find(c => c.value === form.category)) {
      setForm(prev => ({ ...prev, category: "cardio" }));
    } else if (form.exercise_type === "cognitive" && !COGNITIVE_CATEGORIES.find(c => c.value === form.category)) {
      setForm(prev => ({ ...prev, category: "focus" }));
    }
  }, [form.exercise_type, form.category]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error(t("workoutLab.nameRequired"));
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="glass-card p-6 sm:p-8 space-y-5 animate-fade-in" data-testid="exercise-form">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-medium text-sky-800" style={{ fontFamily: "Outfit" }}>
          {exercise ? t("workoutLab.editExercise") : t("workoutLab.createExercise")}
        </h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-600" data-testid="form-cancel-btn">
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <div>
        <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.exerciseName")}</Label>
        <Input
          value={form.name}
          onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
          placeholder={t("workoutLab.namePlaceholder")}
          className="border-sky-200 focus:border-sky-400"
          data-testid="exercise-name-input"
        />
      </div>

      <div>
        <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.type")}</Label>
        <div className="flex gap-2">
          {["physical", "cognitive"].map(type => (
            <button
              key={type}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, exercise_type: type }))}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition-all ${
                form.exercise_type === type
                  ? type === "physical" ? "border-cyan-400 bg-cyan-50 text-cyan-700" : "border-indigo-400 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"
              }`}
              data-testid={`type-${type}-btn`}
            >
              {type === "physical" ? <Dumbbell className="w-4 h-4" strokeWidth={1.5} /> : <Brain className="w-4 h-4" strokeWidth={1.5} />}
              {type === "physical" ? t("workoutLab.physical") : t("workoutLab.cognitive")}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.category")}</Label>
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, category: cat.value }))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                form.category === cat.value
                  ? "bg-sky-400 text-white"
                  : "bg-sky-50 text-sky-600 hover:bg-sky-100"
              }`}
              data-testid={`category-${cat.value}-btn`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.duration")}</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={180}
              value={form.duration_minutes}
              onChange={e => setForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 15 }))}
              className="border-sky-200 focus:border-sky-400 w-20"
              data-testid="exercise-duration-input"
            />
            <span className="text-xs text-slate-400">{t("common.minutes")}</span>
          </div>
        </div>
        {form.exercise_type === "physical" && (
          <div>
            <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.intensity")}</Label>
            <div className="flex gap-1.5">
              {INTENSITIES.map(int => (
                <button
                  key={int.value}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, intensity: int.value }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    form.intensity === int.value ? int.color + " ring-1 ring-current" : "bg-slate-50 text-slate-400"
                  }`}
                  data-testid={`intensity-${int.value}-btn`}
                >
                  {int.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.description")}</Label>
        <Textarea
          value={form.description}
          onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
          placeholder={t("workoutLab.descriptionPlaceholder")}
          rows={2}
          className="border-sky-200 focus:border-sky-400 resize-none"
          data-testid="exercise-description-input"
        />
      </div>

      <div>
        <Label className="text-sm text-sky-700 mb-1.5 block">{t("workoutLab.instructions")}</Label>
        <Textarea
          value={form.instructions}
          onChange={e => setForm(prev => ({ ...prev, instructions: e.target.value }))}
          placeholder={t("workoutLab.instructionsPlaceholder")}
          rows={3}
          className="border-sky-200 focus:border-sky-400 resize-none"
          data-testid="exercise-instructions-input"
        />
      </div>

      <Button type="submit" disabled={saving} className="w-full glow-button" data-testid="exercise-save-btn">
        {saving ? (
          <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
        ) : (
          <>
            <Check className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {exercise ? t("workoutLab.saveChanges") : t("workoutLab.create")}
          </>
        )}
      </Button>
    </form>
  );
};

export const ExerciseCard = ({ exercise, onEdit, onDelete, onAddToPlan }) => {
  const { t } = useTranslation();
  const isPhysical = exercise.exercise_type === "physical";
  const intensity = INTENSITIES.find(i => i.value === exercise.intensity);

  return (
    <div className="glass-card p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(56,189,248,0.1)]" data-testid={`exercise-card-${exercise.id}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl shrink-0 ${isPhysical ? "bg-cyan-50" : "bg-indigo-50"}`}>
          {isPhysical ? (
            <Dumbbell className="w-5 h-5 text-cyan-500" strokeWidth={1.5} />
          ) : (
            <Brain className="w-5 h-5 text-indigo-500" strokeWidth={1.5} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-medium text-sky-900 truncate" style={{ fontFamily: "Outfit" }}>
            {exercise.name}
          </h4>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" strokeWidth={1.5} />
              {exercise.duration_minutes} {t("common.minutes")}
            </span>
            <span className={`px-2 py-0.5 rounded-full ${isPhysical ? "bg-cyan-50 text-cyan-600" : "bg-indigo-50 text-indigo-600"}`}>
              {exercise.category}
            </span>
            {isPhysical && intensity && (
              <span className={`px-2 py-0.5 rounded-full ${intensity.color}`}>
                {intensity.label}
              </span>
            )}
          </div>
          {exercise.description && (
            <p className="text-xs text-slate-500 mt-2 line-clamp-2">{exercise.description}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAddToPlan(exercise)}
          className="text-sky-500 hover:text-sky-700 hover:bg-sky-50 flex-1 text-xs"
          data-testid={`add-to-plan-${exercise.id}`}
        >
          <ListPlus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
          {t("workoutLab.addToPlan")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(exercise)}
          className="text-slate-400 hover:text-sky-600 hover:bg-sky-50"
          data-testid={`edit-exercise-${exercise.id}`}
        >
          <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(exercise.id)}
          className="text-slate-400 hover:text-red-500 hover:bg-red-50"
          data-testid={`delete-exercise-${exercise.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
        </Button>
      </div>
    </div>
  );
};
